import { z } from 'zod';
import { AttributeDataType, CustomAttributeKey } from '../catalog/attributes.ts';
import { WritePolicy } from './catalog-trust.ts';

export const AttributeStatus = z.enum(['unreviewed', 'active', 'deprecated', 'blocked']);
export type AttributeStatus = z.infer<typeof AttributeStatus>;

/** Allowed status changes of a custom attribute (BB-28). */
export const ATTRIBUTE_STATUS_TRANSITIONS: Record<AttributeStatus, readonly AttributeStatus[]> = {
  unreviewed: ['active', 'blocked'],
  active: ['deprecated', 'blocked'],
  deprecated: ['active', 'blocked'],
  blocked: ['active'],
};

export const AttributeProducer = z.enum(['sdk', 'server', 'both', 'import', 'unknown']);
export type AttributeProducer = z.infer<typeof AttributeProducer>;
export const PiiClass = z.enum(['none', 'pii']);
export type PiiClass = z.infer<typeof PiiClass>;
export const PiiClassificationSource = z.enum(['default', 'known_key', 'heuristic', 'manual']);
export type PiiClassificationSource = z.infer<typeof PiiClassificationSource>;

/** Minimum length of the reason for lowering an attribute from `pii` to `none`. */
export const PII_DOWNGRADE_REASON_MIN = 10;

export const AttributeListQuery = z.object({
  status: AttributeStatus.optional(),
  piiClass: PiiClass.optional(),
  reviewRequired: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  tag: z.string().trim().max(64).optional(),
  query: z.string().trim().max(255).optional(),
  cursor: z.string().max(255).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AttributeListQuery = z.infer<typeof AttributeListQuery>;

const Description = z.string().max(2000).nullable();
const Owner = z.string().max(120).nullable();

export const DeclareAttributeBody = z.strictObject({
  key: CustomAttributeKey,
  dataType: AttributeDataType,
  maxArrayLength: z.number().int().min(1).max(500).optional(),
  description: Description.optional(),
  owner: Owner.optional(),
  producer: AttributeProducer.optional(),
});
export type DeclareAttributeBody = z.infer<typeof DeclareAttributeBody>;

export const UpdateAttributeBody = z
  .strictObject({
    dataType: AttributeDataType.optional(),
    description: Description.optional(),
    owner: Owner.optional(),
    producer: AttributeProducer.optional(),
    status: AttributeStatus.optional(),
    piiClass: PiiClass.optional(),
    /** Changing it needs `catalog.edit`; loosening it needs a `reason` (BB-33). */
    writePolicy: WritePolicy.optional(),
    /** Required when `piiClass` goes from `pii` to `none` or the write policy is loosened. */
    reason: z.string().trim().max(500).optional(),
    deprecationNote: z.string().max(500).nullable().optional(),
    replacedBy: z.string().max(255).nullable().optional(),
  })
  .refine((b) => Object.keys(b).some((k) => k !== 'reason'), 'nothing to change');
export type UpdateAttributeBody = z.infer<typeof UpdateAttributeBody>;
